/**
 * Key-value database with caching, versioning, and expiration support
 * @example
 * ```ts
 * const cache = new KvCache(new IndexedDBKv(), {
 *   version: 1,
 *   prefix: 'myapp:',
 *   ttl: 300000, // 5 minutes
 *   maxIdleTime: 60000 // 1 minute
 * });
 * ```
 */
export class KvCache {
    db;
    options;
    /** Creates a new cache instance */
    constructor(db, options) {
        this.db = db;
        this.options = options;
    }
    /** Retrieves a cached value, checking expiration and version */
    async get(key) {
        const item = await this.db.get(this.getKey(key));
        if (!isKvCacheItem(item))
            return null;
        // Check version
        if (item.version !== this.options.version)
            return null;
        const time = this.options.getTime?.() ?? Date.now();
        // Check TTL
        if (typeof this.options.ttl === "number" && item.updatedAt + this.options.ttl < time) {
            return null;
        }
        // Check last accessed time
        if (typeof this.options.maxIdleTime === "number" && item.lastAccessedAt + this.options.maxIdleTime < time) {
            return null;
        }
        // Update last accessed time if needed
        if (typeof this.options.maxIdleTime === "number" && item.lastAccessedAt !== time) {
            await this.db.set(this.getKey(key), { ...item, lastAccessedAt: time });
        }
        return item.data;
    }
    /** Stores a value in the cache with current timestamp and version */
    set(key, value) {
        const time = this.options.getTime?.() ?? Date.now();
        return this.db.set(this.getKey(key), {
            data: value,
            version: this.options.version ?? 0,
            updatedAt: time,
            lastAccessedAt: time,
        });
    }
    /** Removes a cached value */
    del(key) {
        return this.db.del(this.getKey(key));
    }
    /** Removes all cached values */
    clear() {
        return this.db.clear();
    }
    /** Generates the full key with prefix */
    getKey(key) {
        return `${this.options.prefix ?? ""}${String(key)}`;
    }
}
// biome-ignore lint/suspicious/noExplicitAny: no type
const isKvCacheItem = (x) => x !== null && typeof x === "object" && "data" in x && "version" in x && "updatedAt" in x && "lastAccessedAt" in x;
//# sourceMappingURL=cache.js.map